import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, RefreshCw, ShieldCheck } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const API = API_BASE;

const EVIDENCE_LABELS = {
  reply_context: 'reply context in this workspace',
  delivery_status: 'delivery record in this workspace',
  sender_history: 'existing conversation in this workspace',
};

export function sanitizeWhatsAppQuarantineItem(item = {}) {
  return {
    id: String(item.id || ''),
    reasonCode: String(item.reason_code || 'other'),
    reasonLabel: String(item.reason_label || 'WhatsApp event needs review'),
    eventKind: String(item.event_kind || 'inbound'),
    candidateEvidence: String(item.candidate_evidence || ''),
    messageType: item.message_type ? String(item.message_type) : null,
    deliveryStatus: item.delivery_status ? String(item.delivery_status) : null,
    createdAt: item.created_at || null,
    payloadRedacted: item.payload_redacted !== false,
  };
}

export function summarizeWhatsAppQuarantine(payload) {
  const source = Array.isArray(payload?.items) ? payload.items : [];
  const items = source.map(sanitizeWhatsAppQuarantineItem);
  return {
    items,
    visibleCount: Number(payload?.visible_count ?? items.length) || 0,
    redacted: payload?.redacted !== false,
    note: String(payload?.note || ''),
  };
}

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function evidenceLabel(value) {
  return EVIDENCE_LABELS[value] || 'candidate evidence in this workspace';
}

export default function WhatsAppQuarantinePanel() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `${API}/onboarding-communications/whatsapp/quarantine?limit=20`,
        { headers },
      );
      setPayload(response.data || null);
    } catch (requestError) {
      console.warn('Could not load WhatsApp quarantine view', requestError);
      setError(requestError?.response?.data?.detail || 'WhatsApp quarantine status is unavailable');
    } finally {
      setLoading(false);
    }
  }, [headers, token]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = summarizeWhatsAppQuarantine(payload);

  return (
    <div
      className="mt-4 rounded-xl border border-slate-200 bg-white p-4"
      data-testid="whatsapp-quarantine-panel"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            WhatsApp webhook quarantine
          </p>
          <p className="mt-1 text-xs text-slate-600">
            Only redacted events with candidate evidence in this workspace are visible here. Unmatched or unrelated tenant events stay hidden.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={summary.visibleCount > 0
            ? 'border-amber-300 bg-amber-50 text-amber-800'
            : 'border-emerald-300 bg-emerald-50 text-emerald-800'}
          >
            {summary.visibleCount} visible
          </Badge>
          <Button type="button" variant="ghost" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="mt-3 text-xs text-slate-500">Checking workspace-related WhatsApp quarantine events…</p>
      ) : error ? (
        <p className="mt-3 text-xs text-red-700">{error}</p>
      ) : summary.items.length === 0 ? (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
          No quarantined WhatsApp event currently has candidate evidence in this workspace.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {summary.items.map((item) => (
            <div key={item.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold text-amber-950">{item.reasonLabel}</p>
                  <p className="mt-1 text-[11px] text-amber-800">
                    Candidate match: {evidenceLabel(item.candidateEvidence)}
                  </p>
                </div>
                <Badge variant="outline" className="border-amber-300 bg-white text-amber-800">
                  {item.eventKind === 'delivery_status' ? 'Delivery status' : 'Inbound'}
                </Badge>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600">
                {item.deliveryStatus ? <span>Status: {item.deliveryStatus}</span> : null}
                {item.messageType ? <span>Type: {item.messageType}</span> : null}
                {item.createdAt ? <span>{formatDateTime(item.createdAt)}</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" />
        <p>
          Applicant phone numbers, message content, provider message IDs and cross-tenant candidate details are intentionally hidden. This view is for investigation only; it does not reassign or resend messages.
        </p>
      </div>
    </div>
  );
}
